/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import {
  CalendarX2,
  FastForward,
  HelpCircle,
  MessageCircleQuestion,
  OctagonMinus,
  ThumbsDown,
  ThumbsUp,
  UserMinus,
  Users,
  type LucideIcon,
} from "lucide-react";
import type { ApiResult } from "../api/client";
import {
  advanceTimers,
  resetDemo,
  runFreedSlot,
  seedOffer,
  seedTransition,
  sendReply,
} from "../api/demo";

export type Scenario = {
  id: string;
  icon: LucideIcon;
  title: string;
  description: string;
  watch: string;
  toast: string;
  needsSecondPhone?: boolean;
  run: () => Promise<string>;
};

async function must<T>(call: Promise<ApiResult<T>>): Promise<T> {
  const result = await call;
  if (!result.ok) {
    throw new Error(result.error.message);
  }
  return result.value;
}

async function say(call: Promise<ApiResult<unknown>>, outcome: string): Promise<string> {
  await must(call);
  return outcome;
}

/** What each scheduling agent card does against the staging demo routes, and what it then says. */
export function demoScenarios({
  toPhone,
  secondPhone,
  bookablePatientHealthieId,
  dropRunsOf,
}: {
  toPhone: string;
  secondPhone: string;
  bookablePatientHealthieId: string;
  dropRunsOf: (phone: string) => void;
}) {
  // Each reply case seeds its own offer first, so a button never does nothing for want of one.
  const seedThenReply = async (body: string, outcome: string) => {
    await must(seedOffer(toPhone, { patientHealthieId: bookablePatientHealthieId }));
    return say(sendReply(toPhone, body), outcome);
  };
  // A reset that answered ended what it could, so those cards go even if one onboarding held out.
  const resetOrThrow = async () => {
    const { onboardingsEnded, onboardingsFailed } = await must(resetDemo(toPhone));
    dropRunsOf(toPhone);
    if (onboardingsFailed > 0) {
      throw new Error(
        `Reset, but ${onboardingsFailed} of ${onboardingsEnded} demo onboardings couldn't be ended. Try again.`,
      );
    }
    return onboardingsEnded;
  };
  const reset = {
    id: "reset",
    toast: "Demo reset",
    run: async () => {
      const ended = await resetOrThrow();
      return `Reset done. ${ended} demo ${ended === 1 ? "onboarding" : "onboardings"} ended.`;
    },
  };
  const scheduling: Scenario[] = [
    {
      id: "freed-slot",
      icon: CalendarX2,
      title: "A slot frees up",
      description: "Books an intake in Healthie, then cancels it. The cancellation wakes the agent.",
      watch: "An offer text within a minute. The run appears under Agents > Runs.",
      toast: "Slot freed",
      run: () => say(runFreedSlot(toPhone), "Slot freed. The offer text arrives within a minute."),
    },
    {
      id: "reply-yes",
      icon: ThumbsUp,
      title: "Family replies YES",
      description: "Sends an offer, then replies YES.",
      watch: "The intake booked in Healthie, a confirmation text, and the run ending in Booked.",
      toast: "YES sent",
      run: () => seedThenReply("YES", "YES sent. Booking and confirmation follow in seconds."),
    },
    {
      id: "reply-no",
      icon: ThumbsDown,
      title: "Family replies NO",
      description: "Sends an offer, then declines in plain words.",
      watch: "The run records the decline and moves to the next family.",
      toast: "Decline sent",
      run: () =>
        seedThenReply(
          "No thanks, we already found another provider",
          "Decline sent. The agent moves to the next family.",
        ),
    },
    {
      id: "reply-question",
      icon: MessageCircleQuestion,
      title: "Family asks a question",
      description: "Sends an offer, then replies with a question.",
      watch: "Action Required in both inboxes, with the family's words and a Healthie link.",
      toast: "Question sent",
      run: () =>
        seedThenReply(
          "maybe, is that covered by our insurance? what time exactly",
          "Question sent. The case reaches a coordinator within a minute.",
        ),
    },
    {
      id: "reply-stop",
      icon: OctagonMinus,
      title: "Family texts STOP",
      description: "Sends an offer, then texts STOP.",
      watch: "The offer is withdrawn and the phone is opted out until you reset.",
      toast: "STOP sent",
      run: () => seedThenReply("STOP", "STOP sent. The phone is opted out."),
    },
    {
      id: "low-confidence",
      icon: HelpCircle,
      title: "Low confidence",
      description: "Sends an offer to a low-ranked family with a thin profile, then replies YES.",
      watch: "No booking. Action Required in both inboxes, with the score and reasoning.",
      toast: "YES sent",
      run: async () => {
        await must(seedOffer(toPhone, { rank: 2, score: "0.40" }));
        return say(sendReply(toPhone, "YES"), "YES sent. The case goes to a coordinator.");
      },
    },
    {
      id: "multi-match",
      icon: Users,
      title: "No reply, next family",
      description: "Frees a slot two families match. The top family gets the first offer.",
      watch: "An offer to the first phone. Skip the 1-hour wait to send the next one to the second.",
      toast: "Slot freed",
      needsSecondPhone: true,
      run: () =>
        say(runFreedSlot(toPhone, secondPhone), "Slot freed. The first offer is going out."),
    },
    {
      id: "provider-transition",
      icon: UserMinus,
      title: "A provider leaves",
      description: "Resets, then frees a slot for a family losing their provider.",
      watch: "That family ranks first, tagged provider transition, and gets the offer.",
      toast: "Slot freed",
      run: async () => {
        await resetOrThrow();
        await must(seedTransition());
        return say(
          runFreedSlot(toPhone),
          "Slot freed. The offer text arrives within a minute.",
        );
      },
    },
    {
      id: "advance-timers",
      icon: FastForward,
      title: "Skip the 1-hour wait",
      description: "Expires the open offer so the agent moves on.",
      watch: "Within 30 seconds the offer times out and the next family gets a text.",
      toast: "Wait skipped",
      run: () =>
        say(advanceTimers(), "Wait skipped. The next family gets a text within 30 seconds."),
    },
  ];
  return { reset, scheduling };
}
